/**
 * Position Value Object
 * Представляет неизменяемую позицию на игровой доске
 */
export class Position {
    _x;
    _y;
    constructor(x, y) {
        if (x < 0 || x > 10 || y < 0 || y > 10) {
            throw new Error(`Invalid position: (${x}, ${y}). Position must be within 0-10 range.`);
        }
        if (!Number.isInteger(x) || !Number.isInteger(y)) {
            throw new Error(`Invalid position: (${x}, ${y}). Coordinates must be integers.`);
        }
        this._x = x;
        this._y = y;
    }
    get x() {
        return this._x;
    }
    get y() {
        return this._y;
    }
    /**
     * Проверяет равенство позиций
     */
    equals(other) {
        return this._x === other._x && this._y === other._y;
    }
    /**
     * Check if position is valid (within board bounds)
     */
    isValid() {
        return this._x >= 0 && this._x <= 10 && this._y >= 0 && this._y <= 10;
    }
    /**
     * Возвращает все соседние позиции (ортогональные)
     */
    getOrthogonalNeighbors() {
        const neighbors = [];
        const offsets = [
            { dx: 0, dy: -1 }, // вверх
            { dx: 1, dy: 0 }, // вправо
            { dx: 0, dy: 1 }, // вниз
            { dx: -1, dy: 0 }, // влево
        ];
        for (const { dx, dy } of offsets) {
            const newX = this._x + dx;
            const newY = this._y + dy;
            if (newX >= 0 && newX <= 10 && newY >= 0 && newY <= 10) {
                neighbors.push(new Position(newX, newY));
            }
        }
        return neighbors;
    }
    /**
     * Get diagonal neighbor positions
     */
    getDiagonalNeighbors() {
        const neighbors = [];
        const offsets = [
            { dx: -1, dy: -1 }, // top-left
            { dx: 1, dy: -1 }, // top-right
            { dx: 1, dy: 1 }, // bottom-right
            { dx: -1, dy: 1 }, // bottom-left
        ];
        for (const { dx, dy } of offsets) {
            const newX = this._x + dx;
            const newY = this._y + dy;
            if (newX >= 0 && newX <= 10 && newY >= 0 && newY <= 10) {
                neighbors.push(new Position(newX, newY));
            }
        }
        return neighbors;
    }
    /**
     * Возвращает все позиции, доступные ходом конём
     */
    getKnightMoves() {
        const moves = [];
        const knightOffsets = [
            { dx: -2, dy: -1 },
            { dx: -2, dy: 1 },
            { dx: -1, dy: -2 },
            { dx: -1, dy: 2 },
            { dx: 1, dy: -2 },
            { dx: 1, dy: 2 },
            { dx: 2, dy: -1 },
            { dx: 2, dy: 1 },
        ];
        for (const { dx, dy } of knightOffsets) {
            const newX = this._x + dx;
            const newY = this._y + dy;
            if (newX >= 0 && newX <= 10 && newY >= 0 && newY <= 10) {
                moves.push(new Position(newX, newY));
            }
        }
        return moves;
    }
    /**
     * Проверяет, находится ли позиция на границе доски
     */
    isBorder() {
        return this._x === 0 || this._x === 10 || this._y === 0 || this._y === 10;
    }
    /**
     * Возвращает уникальный ключ позиции
     */
    toKey() {
        return `${this._x},${this._y}`;
    }
    /**
     * Создаёт позицию из ключа
     */
    static fromKey(key) {
        const parts = key.split(',');
        if (parts.length !== 2) {
            throw new Error(`Invalid position key: ${key}`);
        }
        const x = Number(parts[0]);
        const y = Number(parts[1]);
        if (isNaN(x) || isNaN(y)) {
            throw new Error(`Invalid position key: ${key}`);
        }
        return new Position(x, y);
    }
    toString() {
        return `(${this._x}, ${this._y})`;
    }
}
//# sourceMappingURL=Position.js.map